import assert from 'assert';
import has from 'lodash/has';

import {
  isPending,
  toObjectHash,
  MusterError,
} from '../../../muster-utils';

export const FRAGMENTS_SYMBOL = '$$fragments';

export function serialize(data) {
  return JSON.stringify(data, null, 2);
}

export function isList(node) {
  return (node.$type === 'list');
}

export function isStaticList(node) {
  return isList(node) && (
    (typeof node.$range.from === 'number' && typeof node.$range.to === 'number') ||
    (typeof node.$range.offset === 'number' && typeof node.$range.length === 'number')
  );
}

export function isDynamicList(node) {
  return isList(node) && !isStaticList(node);
}

export function isFragment(node) {
  return (node.$type === 'fragment');
}

export function isMap(node) {
  return (node.$type === 'map');
}

export function isCallOrSet(node) {
  return (node.$set !== undefined || node.$call !== undefined);
}

export function isLeafNode(value) {
  return (
    (typeof value === 'string') || (value === true)
  );
}

export function defer(node) {
  assert(!isCallOrSet(node), 'Cannot defer a fragment');
  assert(!isDeferred(node), 'Cannot wrap a deferred node in `defer`');

  return {
    $type: 'defer',
    $node: node,
  };
}

export function isDeferred(value) {
  return Boolean(value) && value.$type === 'defer';
}

// Join array of paths together as a single path
export function joinPathSegments(...args) {
  return args.filter((value) => value).join('.');
}

export function initPathValues(pathValues) {
  return addPathValues(pathValues, new Map());
}

export function addPathValues(pathValues, cache) {
  pathValues.forEach(({ path, value }) => { cache.set(path, value); });
  return cache;
}

export function parseStaticRange(range) {
  const from = has(range, 'from') ? range.from : range.offset;
  const to = has(range, 'to') ? range.to : range.offset + range.length - 1;

  assert(typeof from === 'number' && typeof to === 'number', (
    'List range received incompatible `from` or `to` values. ' +
    `Expected \`{ from: Number, to: Number }\` instead recieved ${serialize(range)}`
  ));

  return { from, to };
}

export function when(predicate, ...funcs) {
  return {
    predicate,
    func: (memo) => {
      if (predicate) {
        return funcs.reduce((innerMemo, func) => func(innerMemo), memo);
      }
      return memo;
    },
  };
}

export function otherwise(...funcs) {
  return when(() => true, ...funcs);
}

/**
 * @param {Map} pathValues - Path value map
 * @returns {Object} - Nested tree representing the pathValues
 */
export function formatPathValues(pathValues) {
  const memo = [];
  for (const [path, value] of pathValues) {
    if (isPending(value)) {
      memo.push({ path, value: 'PENDING' });
    } else {
      memo.push({ path, value });
    }
  }
  return toObjectHash(memo);
}

/**
 * @param {Map} pathValues - Path value map
 * @returns {Array} - Array of paths that are pending
 */
export function pendingPaths(pathValues) {
  return [...pathValues]
    .filter((pathValue) => isPending(pathValue[1]))
    .map((pathValue) => pathValue[0]);
}

function repeat(str, times) {
  return (new Array(times + 1)).join(str);
}

function pad(num, maxLength) {
  return repeat('0', maxLength - num.toString().length) + num;
}

/**
 * @param {Date} time - Date to format
 * @returns {String} - Formatted date string
 */
export function formatTime(time) {
  return (
    `${pad(time.getHours(), 2)}:${pad(time.getMinutes(), 2)}:` +
    `${pad(time.getSeconds(), 2)}.${pad(time.getMilliseconds(), 3)}`
  );
}

/**
 * @param {Function} filter - Filter function to apply to path
 * @param {Map} pathValues - Path value map
 * @returns {Map} - Filtered pathValue object
 */
export function filterPathValues(filter, pathValues) {
  return [...pathValues.entries()].reduce((memo, [path, value]) => {
    if (filter(path)) {
      memo.set(path, value);
    }
    return memo;
  }, new Map());
}

/**
 * @param {Function} replacer - Function to apply to path
 * @param {Map} pathValues - Path value map
 * @returns {Map} - Filtered pathValue object
 */
export function replacePath(replacer, pathValues) {
  return [...pathValues.entries()].reduce((memo, [path, value]) => (
    memo.set(replacer(path), value)
  ), new Map());
}

/**
 * @param {Object} serializedError - Serialized error
 * @returns {MusterError} - MusterError instance
 */
export function deserializeMusterError(serializedError) {
  return new MusterError(serializedError.message, serializedError);
}

/**
 * Use console.groupCollapsed if it exists
 * @returns {Void} - N/A
 */
export function consoleGroupCollapsed(...args) {
  /* eslint-disable no-console */
  return console.groupCollapsed === undefined ?
    console.log('--groupStart--', ...args) :
    console.groupCollapsed(...args);
  /* eslint-enable */
}

/**
 * Use console.groupEnd if it exists
 * @returns {Void} - N/A
 */
export function consoleGroupEnd() {
  /* eslint-disable no-console */
  return console.groupEnd === undefined ?
    console.log('--groupEnd--') :
    console.groupEnd();
  /* eslint-enable */
}
